import { useCallback, useEffect, useRef, useState } from 'react';
import { GetClinicStatsAPI } from '../../api/API';
import STATS_SECTION_LIST from './sections';

export const STATS_SECTIONS = STATS_SECTION_LIST.map((section) => section.id);

/**
 * Carrega todas as seções das estatísticas em paralelo, uma chamada por seção,
 * para cada área aparecer assim que fica pronta (sem uma tela de carregamento
 * única que trava tudo quando uma área falha).
 *
 * `period`: { start, end, compare } (compare: none | previous | year).
 * `lockedSections`: seções bloqueadas pelo plano; não chamam a API.
 * `planReady`: false enquanto o plano não chegou. Até lá nada é pedido, para
 * uma seção bloqueada não ir ao servidor (e voltar 403) só porque o plano
 * ainda não tinha sido lido.
 *
 * @returns {{
 *   sections: { [section]: { status: 'loading'|'ready'|'error'|'locked', data, granularity } },
 *   reload: (section) => void
 * }}
 */
export default function useClinicStats(period, lockedSections, planReady = true) {
  const [sections, setSections] = useState({});
  const requestRef = useRef(0);
  const lockedKey = lockedSections.join('|');

  const fetchSection = useCallback((section, request) => {
    setSections((current) => ({ ...current, [section]: { status: 'loading', data: null } }));
    GetClinicStatsAPI(section, period.start, period.end, period.compare, (response) => {
      // Resposta de um período antigo (a pessoa trocou o filtro no meio)
      if (requestRef.current !== request) return;
      const ok = response && response.success;
      setSections((current) => ({
        ...current,
        [section]: ok
          ? { status: 'ready', data: response.data, granularity: response.period?.granularity }
          : { status: response?.code === 'BLOCKED_ON_PLAN' ? 'locked' : 'error', data: null },
      }));
    });
  }, [period.start, period.end, period.compare]);

  useEffect(() => {
    const request = requestRef.current + 1;
    requestRef.current = request;

    const initial = {};
    STATS_SECTIONS.forEach((section) => {
      const locked = planReady && lockedSections.includes(section);
      initial[section] = { status: locked ? 'locked' : 'loading', data: null };
    });
    setSections(initial);
    if (!planReady) return;

    STATS_SECTIONS
      .filter((section) => !lockedSections.includes(section))
      .forEach((section) => fetchSection(section, request));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSection, lockedKey, planReady]);

  const reload = useCallback((section) => fetchSection(section, requestRef.current), [fetchSection]);

  return { sections, reload };
}
